import { Grid } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { KBLinks } from 'features/constants';
import React, { useContext, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import AgentNetDivider from 'ui-kit/components/dividers/AgentNetDivider2';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import RadioGroup, { RadioProps } from 'ui-kit/components/radios/RadioGroup';
import PaymentCards from './PaymentCards';
import { getPaymentIframe } from 'api/payment/api';
import useAsync from 'hooks/useAsync';
import { useAuth } from '@agentnet/auth';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { sanitizeUrl } from 'utilities/utilities';
import { useEpayAccounts } from './useEpayAccounts';

const paymentMode = [
  { label: 'Credit or Debit Card', value: 'CC' },
  { label: 'ACH Transfer/Bank Account', value: 'ACH' },
];

const useStyles = makeStyles(() =>
  createStyles({
    responsiveGrid: {
      transition: 'all 0.3s ease-in-out',
      padding: '0 24px',
      flexGrow: 1,
      margin: 0,
      width: '100%',
    },
    heading: {
      marginBottom: '24px',
    },
    iframe: {
      border: '0px #ffffff none',
      position: 'absolute',
      left: '8px',
      width: '100%',
      maxWidth: '370px',
      minHeight: '800px',
      marginTop: '16px',
    },
  }),
);

const PaymentMethod = () => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm } = profileCtx;

  const [openDrawer, setDrawer] = useState(false);
  const [isAch, setAch] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<RadioProps>(paymentMode[0]);

  const { accounts, status: paymentMethodStatus } = useEpayAccounts();

  const handleOpenDrawer = () => {
    setDrawer(true);
    executeframeData().then();
  };

  const closeDrawer = () => {
    setDrawer(false);
  };

  const handlePaymentMethod = (e: React.ChangeEvent<HTMLInputElement>) => {
    const paymentMethod = paymentMode?.find((option) => option.value === e.target.value) ?? selectedPaymentMode;
    setAch(paymentMethod.value === 'ACH');
    setSelectedPaymentMode(paymentMethod);
  };

  const getIframeData = async () => {
    const token = await getAccessToken();
    return await getPaymentIframe(token, userFirm?.firmId ?? '', isAch);
  };

  const {
    execute: executeframeData,
    status: executeframeDataStatus,
    value: frameDataResults,
  } = useAsync<any>(getIframeData, false);

  return (
    <ContentContainer>
      <PageHeader
        title="Payment Methods"
        subtitle="Manage payment methods for use when electronically paying Premiums and Service Order Invoices."
        contentRight={
          <AgentNetButton
            variant="contained"
            onClick={handleOpenDrawer}
            plusIcon
            color="primary"
            size="small"
            data-testid="payment-method-button-add-payment-method-top"
          >
            Add Payment Method
          </AgentNetButton>
        }
        menuItems={[
          {
            label: 'Knowledge Base',
            link: KBLinks.billing,
          },
        ]}
      />
      <Grid container spacing={2} className={classes.responsiveGrid}>
        {paymentMethodStatus === 'pending' && <LoadingSpinner variant="circle" status={'pending'} />}

        {paymentMethodStatus === 'success' && (
          <PaymentCards paymentMethodData={accounts} handleOpenDrawer={handleOpenDrawer} isPaymentMethodView />
        )}
      </Grid>
      <FormDrawerComponent
        title="Add New Payment Method"
        width={386}
        open={openDrawer}
        disableEnforceFocus
        crossQAPrefix="PaymentMethodDrawer"
        isPaddingRightZero
        showDrawerActions={false}
        onDismissAction={closeDrawer}
      >
        {executeframeDataStatus === 'pending' && <LoadingSpinner variant="circle" status={'pending'} />}
        {executeframeDataStatus === 'success' && (
          <>
            <Grid item sm={12} className={classes.heading}>
              <AgentNetDivider typoVariant="h3" title={'Payment Method'} disablePadding />
            </Grid>
            <Grid item sm={12}>
              <RadioGroup selected={selectedPaymentMode} options={paymentMode} onChange={handlePaymentMethod} />
            </Grid>
            <Grid item sm={12}>
              <iframe
                src={sanitizeUrl(frameDataResults?.sessionTransferUrl)}
                className={classes.iframe}
                scrolling="no"
                height="800px"
                width="370px"
              ></iframe>
            </Grid>
          </>
        )}
      </FormDrawerComponent>
    </ContentContainer>
  );
};

export default PaymentMethod;
